import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Plus, Trash, Warning } from '@phosphor-icons/react';
import { patchTrip } from '../lib/api';
import { assignDivision, formatDivisionHandicap } from '../golf';

/** Parse user-typed handicap string → stored float.
 *  "+6" or "-6" → -6 (plus handicap). "10.1" → 10.1. "" → null. */
const parseHcap = (str) => {
  const s = str.trim();
  if (s === '') return null;
  if (s.startsWith('+')) { const n = parseFloat(s.slice(1)); return Number.isFinite(n) ? -n : null; }
  if (s.startsWith('-')) { const n = parseFloat(s.slice(1)); return Number.isFinite(n) ? -n : null; }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
};

/** Stored float → display string. Negative → "+N". 999 sentinel → "". */
const displayHcap = (v) => {
  if (v == null) return '';
  if (v <= -999) return '';
  if (v >= 999) return '';
  if (v < 0) return `+${Math.abs(v)}`;
  return String(v);
};

/** Is a stored value a plus handicap (negative)? */
const isPlus = (v) => v != null && v < 0 && v > -999;

/** Small input with a +/- toggle for handicap entry. */
function HcapInput({ value, onChange, placeholder, allowBlank, blankLabel, testId }) {
  // Local editing state: the raw text the user sees
  const [text, setText] = useState(() => displayHcap(value));
  const [plus, setPlus] = useState(() => isPlus(value));

  // Sync from parent when value changes externally
  useEffect(() => {
    setText(displayHcap(value));
    setPlus(isPlus(value));
  }, [value]);

  const commit = (rawText, plusFlag) => {
    const s = rawText.trim();
    if (s === '' && allowBlank) { onChange(999); return; } // "and above" sentinel
    if (s === '') { onChange(null); return; }
    // Strip leading + or - the user typed (we use the toggle for sign)
    let cleaned = s.replace(/^[+-]/, '');
    const n = parseFloat(cleaned);
    if (!Number.isFinite(n)) return; // invalid — don't commit
    onChange(plusFlag ? -Math.abs(n) : Math.abs(n));
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    setText(raw);
    // Auto-detect plus from typing
    const trimmed = raw.trim();
    if (trimmed.startsWith('+') || trimmed.startsWith('-')) {
      setPlus(true);
      commit(trimmed, true);
    } else {
      commit(trimmed, plus);
    }
  };

  const togglePlus = () => {
    const next = !plus;
    setPlus(next);
    commit(text, next);
  };

  const handleBlur = () => {
    // Re-format on blur
    setText(displayHcap(value));
    setPlus(isPlus(value));
  };

  const isBlankSentinel = value === 999;

  return (
    <div className="flex items-center gap-0.5">
      <button type="button" onClick={togglePlus}
        className={`h-9 w-7 rounded-l-lg text-xs font-bold flex items-center justify-center flex-shrink-0 transition-colors ${plus ? 'bg-[#1B4332] text-white' : 'bg-[#E8E5DB] text-[#455561]'}`}
        title={plus ? 'Plus handicap (on)' : 'Plus handicap (off)'}
        data-testid={`${testId}-plus-toggle`}>+</button>
      <div className="relative">
        <Input
          value={isBlankSentinel ? '' : text}
          placeholder={isBlankSentinel && blankLabel ? blankLabel : placeholder}
          className="h-9 text-sm w-16 rounded-l-none rounded-r-lg text-center"
          onChange={handleChange}
          onBlur={handleBlur}
          data-testid={testId} />
        {isBlankSentinel && blankLabel && (
          <span className="absolute inset-0 flex items-center justify-center text-[9px] text-[#8A9BA3] font-semibold pointer-events-none">{blankLabel}</span>
        )}
      </div>
    </div>
  );
}


export default function DivisionsEditor({ trip, code, isOrganiser, onUpdate }) {
  const [enabled, setEnabled] = useState(trip?.divisions_enabled || false);
  const [divs, setDivs] = useState(trip?.divisions || []);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(trip?.divisions_enabled || false);
    setDivs(trip?.divisions || []);
  }, [trip?.divisions_enabled, trip?.divisions]);

  if (!isOrganiser) return null;

  const validate = (list) => {
    for (const d of list) {
      if (!d.name?.trim()) return 'Division name is required';
      if (d.min_handicap == null || d.max_handicap == null) return `${d.name}: min and max required`;
      if (d.min_handicap > d.max_handicap) return `${d.name}: min > max`;
    }
    const sorted = [...list].sort((a, b) => a.min_handicap - b.min_handicap);
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i].max_handicap >= sorted[i + 1].min_handicap) {
        return `Overlap: "${sorted[i].name}" max (${formatDivisionHandicap(sorted[i].max_handicap)}) overlaps "${sorted[i + 1].name}" min (${formatDivisionHandicap(sorted[i + 1].min_handicap)})`;
      }
    }
    return '';
  };

  const save = async (newEnabled, newDivs) => {
    if (newEnabled && newDivs.length > 0) {
      const err = validate(newDivs);
      if (err) { setError(err); return; }
    }
    setError('');
    setSaving(true);
    try {
      const res = await patchTrip(code, {
        divisions_enabled: newEnabled,
        divisions: newDivs.length > 0 ? newDivs : null,
      });
      onUpdate(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const toggleEnabled = () => {
    const next = !enabled;
    setEnabled(next);
    save(next, divs);
  };

  const addDiv = () => {
    const next = [...divs, { id: crypto.randomUUID(), name: `Division ${divs.length + 1}`, min_handicap: 0, max_handicap: 54 }];
    setDivs(next);
  };

  const removeDiv = (idx) => {
    const next = divs.filter((_, i) => i !== idx);
    setDivs(next);
    save(enabled, next);
  };

  const updateDiv = (idx, field, val) => {
    const next = divs.map((d, i) => i === idx ? { ...d, [field]: val } : d);
    setDivs(next);
  };

  const saveDivs = () => save(enabled, divs);

  // Warnings: players outside all ranges or with no handicap
  const warnings = [];
  if (enabled && divs.length > 0) {
    for (const p of (trip?.players || []).filter(p => p.active !== false)) {
      const h = p.handicap_mode === 'ph' ? p.manual_ph : p.whs_index;
      if (h == null || h === undefined) {
        warnings.push(`${p.name}: no handicap entered`);
      } else {
        const assigned = assignDivision(p, divs);
        if (!assigned && !p.division_id) {
          warnings.push(`${p.name}: handicap ${formatDivisionHandicap(h)} outside all ranges`);
        }
      }
    }
  }

  return (
    <div className="px-4 pt-4 max-w-lg mx-auto" data-testid="divisions-editor">
      <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#1D242B]">Handicap Divisions</h3>
          <button
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-[#1B4332]' : 'bg-[#B8C7C0]'}`}
            onClick={toggleEnabled}
            data-testid="divisions-toggle"
          >
            <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {enabled && (
          <>
            {/* Column labels */}
            <div className="flex items-center gap-2 mb-1 px-0.5">
              <span className="text-[10px] text-[#8A9BA3] font-semibold flex-1">Name</span>
              <span className="text-[10px] text-[#8A9BA3] font-semibold w-[88px] text-center">From</span>
              <span className="text-[10px] text-[#8A9BA3] font-semibold w-3" />
              <span className="text-[10px] text-[#8A9BA3] font-semibold w-[88px] text-center">To</span>
              <span className="w-6" />
            </div>

            {divs.map((d, idx) => (
              <div key={d.id} className="flex items-center gap-2 mb-2" data-testid={`division-row-${idx}`}>
                <Input value={d.name} placeholder="Name"
                  className="h-9 text-sm flex-1 rounded-lg"
                  onChange={e => updateDiv(idx, 'name', e.target.value)} />
                <HcapInput value={d.min_handicap} placeholder="From"
                  testId={`div-${idx}-min`}
                  onChange={v => { if (v !== null) updateDiv(idx, 'min_handicap', v); }} />
                <span className="text-xs text-[#455561]">to</span>
                <HcapInput value={d.max_handicap} placeholder="To"
                  testId={`div-${idx}-max`}
                  allowBlank blankLabel="& above"
                  onChange={v => { if (v !== null) updateDiv(idx, 'max_handicap', v); }} />
                <button onClick={() => removeDiv(idx)} className="text-[#D32F2F] p-1" data-testid={`remove-div-${idx}`}>
                  <Trash className="w-4 h-4" />
                </button>
              </div>
            ))}

            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={addDiv} className="text-xs" data-testid="add-division-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Division
              </Button>
              <Button size="sm" onClick={saveDivs} disabled={saving} className="text-xs bg-[#1B4332] text-white" data-testid="save-divisions-btn">
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>

            {error && <p className="text-xs text-[#D32F2F] mt-2" data-testid="division-error">{error}</p>}

            {warnings.length > 0 && (
              <div className="mt-3 p-2 bg-[#FFF3E0] rounded-lg" data-testid="division-warnings">
                <div className="flex items-center gap-1 mb-1">
                  <Warning className="w-3.5 h-3.5 text-[#E59000]" weight="fill" />
                  <span className="text-xs font-semibold text-[#E59000]">Unassigned Players</span>
                </div>
                {warnings.map((w, i) => <p key={i} className="text-xs text-[#455561]">{w}</p>)}
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
